import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { api } from "../../../../api/apiClient";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupsIcon from "@mui/icons-material/Groups";
import { useHistory } from "react-router-dom";

import PageHeader from "../../../../app/layout/PageHeader";
import UpsertAudienceTab from "./UpsertAudienceTab";
import "../../../../css/buttons.css";
import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/tables.css";
import "../CommunicationList.css";

interface Audience {
  audienceId: number;
  name: string;
  members?: { partyId: number }[];
  rules?: {
    ruleType: string;
    lookbackDays?: number | null;
    comparisonOperator?: "AtLeast" | "AtMost" | "Exactly" | null;
    threshold?: number | null;
  }[];
}

const getRuleSummary = (rule: NonNullable<Audience["rules"]>[number]) => {
  switch (rule.ruleType) {
    case "AttendedService":
      return rule.lookbackDays
        ? `Attended a service in the last ${rule.lookbackDays} days`
        : "Attended a service";
    case "AttendedSession":
      return "Attended a specific session";
    case "NotAttendedRecently":
      return `Not attended in the last ${rule.lookbackDays ?? "selected"} days`;
    case "NeverAttendedService":
      return "Never attended a service";
    case "AttendanceCount": {
      const comparison = {
        AtLeast: "At least",
        AtMost: "At most",
        Exactly: "Exactly",
      }[rule.comparisonOperator ?? "AtLeast"];
      return `${comparison} ${rule.threshold ?? "the selected number of"} sessions`;
    }
    case "ServiceMember":
      return "Member of a service";
    default:
      return "Automatic selection rule";
  }
};

export default function ManageAudiencesPage() {
  const history = useHistory();
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingAudienceId, setEditingAudienceId] = useState<number | null>(
    null,
  );

  async function refresh() {
    const summaries = await api<Audience[]>(`/api/audience`);
    const audiencesWithDetails = await Promise.all(
      summaries.map(async (summary) => {
        try {
          const details = await api<Audience>(
            `/api/audience/${summary.audienceId}`,
          );

          return {
            ...summary,
            members: details.members ?? [],
            rules: details.rules ?? [],
          };
        } catch {
          return summary;
        }
      }),
    );

    setAudiences(audiencesWithDetails);
  }

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return audiences;

    return audiences.filter((a) => a.name.toLowerCase().includes(term));
  }, [audiences, search]);

  const cancelForm = () => {
    setShowCreate(false);
    setEditingAudienceId(null);
  };

  const deleteAudience = async (audience: Audience) => {
    if (!window.confirm(`Delete audience "${audience.name}"?`)) return;

    await api(`/api/audience/${audience.audienceId}`, {
      method: "DELETE",
      notifySuccess: "Audience deleted successfully",
    });

    cancelForm();
    refresh();
  };

  const isEditing = Boolean(showCreate || editingAudienceId);

  return (
    <div className="page-container">
      <PageHeader
        title="Contact Lists"
        subtitle="Create reusable contact lists."
        actions={
          <div className="service-management-header-actions">
            <button
              className={`icon-button-solid ${
                isEditing
                  ? "icon-button-solid--danger"
                  : "icon-button-solid--success"
              }`}
              type="button"
              onClick={() => {
                setEditingAudienceId(null);
                setShowCreate((prev) => !prev);
              }}
              title={isEditing ? "Cancel" : "Create Contact List"}
            >
              {isEditing ? (
                <CloseIcon fontSize="small" />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </button>
            <button
              type="button"
              className="icon-button-muted-solid"
              onClick={() => history.push("/dashboard")}
              title="Back to dashboard"
              aria-label="Back to dashboard"
            >
              <ArrowBackIcon fontSize="small" />
            </button>
          </div>
        }
      />

      <div className="card">
        <div className="card-header-row">
          <div className="table-actions">
            {!isEditing && (
              <div className="search-container table-search">
                <div className="search-input-wrapper">
                  <SearchIcon className="search-icon-left" fontSize="small" />
                  <input
                    className="form-input search-input-with-icons"
                    placeholder="Search Contact Lists..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="search-clear-button"
                      onClick={() => setSearch("")}
                      aria-label="Clear audience search"
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {isEditing && (
          <UpsertAudienceTab
            audienceId={editingAudienceId}
            onSaved={() => {
              cancelForm();
              refresh();
            }}
            onCancel={cancelForm}
          />
        )}

        {!isEditing && (
          <div className="communication-list">
            {filtered.length === 0 && (
              <div className="communication-list-empty">
                No Contact Lists found
              </div>
            )}
            {filtered.map((audience) => (
              <article
                className="communication-list-item"
                key={audience.audienceId}
              >
                <span className="communication-list-icon communication-list-icon--audience">
                  <GroupsIcon />
                </span>
                <div className="communication-list-copy">
                  <div className="communication-list-title-row">
                    <strong>{audience.name}</strong>
                    {(audience.rules?.length ?? 0) > 0 && (
                      <span className="communication-status communication-status--rule">
                        Rule-based
                      </span>
                    )}
                  </div>
                  {(audience.rules?.length ?? 0) > 0 && (
                    <span>Rule: {getRuleSummary(audience.rules![0])}</span>
                  )}
                  {Array.isArray(audience.rules) &&
                    audience.rules.length === 0 &&
                    Array.isArray(audience.members) && (
                    <span>
                      {audience.members?.length ?? 0}{" "}
                      {(audience.members?.length ?? 0) === 1
                        ? "contact"
                        : "contacts"}
                    </span>
                    )}
                  {!Array.isArray(audience.rules) && <span>Contact list</span>}
                </div>
                <div className="communication-list-actions">
                  <Tooltip title="Edit audience">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreate(false);
                        setEditingAudienceId(audience.audienceId);
                      }}
                      aria-label={`Edit ${audience.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete audience">
                    <button
                      type="button"
                      className="communication-list-action--danger"
                      onClick={() => deleteAudience(audience)}
                      aria-label={`Delete ${audience.name}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  </Tooltip>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
